import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { OrgMember } from '@theseus/shared-ui/orgs'

export function useOrgMembers(orgId: string | undefined) {
  const { data: members = [], isLoading } = useQuery({
    queryKey: ['org-members', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organization_members')
        .select('id, user_id, role, joined_at')
        .eq('org_id', orgId!)
        .is('deleted_at', null)
        .order('joined_at', { ascending: true })

      if (error) {
        console.error('Failed to fetch org members:', error)
        throw error
      }

      if (!data || data.length === 0) return []

      // Fetch profiles separately (no direct FK from org_members to user_profiles)
      const userIds = data.map((m) => m.user_id)
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, full_name')
        .in('id', userIds)

      const profileMap = new Map(
        (profiles ?? []).map((p) => [p.id, p.full_name])
      )

      return data.map((m): OrgMember => ({
        id: m.id,
        userId: m.user_id,
        displayName: profileMap.get(m.user_id) ?? 'Unknown',
        email: '',
        role: m.role as OrgMember['role'],
        joinedAt: m.joined_at,
      }))
    },
    enabled: !!orgId,
  })

  return { members, loading: isLoading }
}
